/** Thrown by pollUntil when the caller's `cancelled` predicate turns true. */
export class PollCancelledError extends Error {
  constructor() {
    super("Polling cancelled")
    this.name = "PollCancelledError"
  }
}

export interface PollOptions {
  /** Delay between attempts (default 3s). */
  intervalMs?: number
  /** Total time budget before the last error is rethrown (default 120s). */
  timeoutMs?: number
  /** Once this much time has elapsed, attempts space out to `slowIntervalMs`
   *  instead of `intervalMs` — a long budget keeps its early attempts snappy
   *  without paying the fast rate for its whole duration. */
  slowAfterMs?: number
  /** The widened interval used after `slowAfterMs` has elapsed. */
  slowIntervalMs?: number
  /** Checked between attempts; returning true aborts with PollCancelledError. */
  cancelled?: () => boolean
}

/**
 * Poll an async operation until it resolves, with a fixed interval and a total
 * time budget. Rejected attempts (e.g. getReport's 404 "not generated yet")
 * are retried until the deadline, then the LAST error is rethrown so the
 * caller can classify it (404 = never generated, 401/403 = auth, else
 * transient) — the same contract the interview arena's inline loop had.
 */
export async function pollUntil<T>(fn: () => Promise<T>, options: PollOptions = {}): Promise<T> {
  const intervalMs = options.intervalMs ?? 3000
  const timeoutMs = options.timeoutMs ?? 120_000
  const slowAfterMs = options.slowAfterMs ?? Infinity
  const slowIntervalMs = options.slowIntervalMs ?? intervalMs
  const startedAt = Date.now()
  for (;;) {
    if (options.cancelled?.()) throw new PollCancelledError()
    try {
      return await fn()
    } catch (error) {
      if (options.cancelled?.()) throw new PollCancelledError()
      const elapsed = Date.now() - startedAt
      if (elapsed >= timeoutMs) throw error
      const interval = elapsed >= slowAfterMs ? slowIntervalMs : intervalMs
      await new Promise((resolve) => setTimeout(resolve, interval))
    }
  }
}
